import { ArrowLeft, Sparkles } from 'lucide-react';
import { motion } from 'motion/react';
import { DESCRIPTION_TEMPLATES } from '../../../lib/constants';

interface Props {
  songName: string;
  songText: string;
  songDescription: string;
  theme: string;
  isLoading: boolean;
  onNameChange: (v: string) => void;
  onTextChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onThemeToggle: (theme: string) => void;
  onGenerateText: () => void;
  onBack: () => void;
  onNext: () => void;
}

const LIMITS = {
  SONG_NAME: 32,
  SONG_DESCRIPTION: 256,
  SONG_TEXT: 3000,
} as const;

export function StepText({
  songName,
  songText,
  songDescription,
  theme,
  isLoading,
  onNameChange,
  onTextChange,
  onDescriptionChange,
  onThemeToggle,
  onGenerateText,
  onBack,
  onNext,
}: Props) {
  const canGenerate: boolean =
    !isLoading &&
    !!songName.trim() &&
    (!!songDescription.trim() || !!theme.trim());
  const canProceed: boolean = !!songName && !!songText;

  return (
    <motion.div
      key="text"
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className="max-w-2xl w-full"
    >
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        Назад
      </button>

      <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-8 text-center bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
        О чём будет ваша песня?
      </h2>

      <div className="bg-white/80 backdrop-blur-md rounded-3xl p-4 sm:p-8 space-y-6 shadow-xl">
        <div className="bg-purple-50/50 rounded-2xl p-4 sm:p-6 border border-purple-100 space-y-4">
          {/* Название песни */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Название песни *
            </label>
            <input
              type="text"
              maxLength={LIMITS.SONG_NAME}
              value={songName}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Введите название трека..."
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none text-gray-900"
            />
            <div className="flex justify-end mt-1">
              <span className="text-xs text-gray-400 font-medium">
                {songName.length}/{LIMITS.SONG_NAME}
              </span>
            </div>
          </div>

          {/* Описание песни */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Опишите тему, настроение или сюжет
            </label>
            <textarea
              maxLength={LIMITS.SONG_DESCRIPTION}
              value={songDescription}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder="Пример: «Мой лучший друг — спортсмен, семьянин и весёлый человек. Сегодня ему 34 года. Желаю ему новых рекордов, крепкого здоровья и счастья в семье. Стиль песни — реп, быстрый темп.»"
              rows={4}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none resize-none text-gray-900 min-h-[100px] sm:min-h-[80px] placeholder:text-sm"
            />
            <div className="flex justify-end mt-1">
              <span className="text-xs text-gray-400 font-medium">
                {songDescription.length}/{LIMITS.SONG_DESCRIPTION}
              </span>
            </div>

            <h4 className="mt-4 mb-2 text-sm font-medium text-gray-700">
              Тема песни
            </h4>
            <div className="flex flex-wrap gap-2 mt-3">
              {DESCRIPTION_TEMPLATES.map(({ text, emoji }) => {
                const isSelected = theme === text;
                return (
                  <button
                    key={text}
                    onClick={() => onThemeToggle(text)}
                    aria-pressed={isSelected}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm rounded-full transition-colors border shadow-sm ${
                      isSelected
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border-transparent shadow-md scale-105'
                        : 'bg-white text-purple-700 hover:bg-purple-100 border-purple-200'
                    }`}
                  >
                    <span>{emoji}</span>
                    <span>{text}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <button
            onClick={onGenerateText}
            disabled={!canGenerate}
            className="mt-2 w-full px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl shadow-md font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Sparkles className="w-5 h-5" />
            {isLoading ? 'Нейросеть пишет текст...' : 'Сгенерировать текст'}
          </button>
        </div>

        {/* Текст песни */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Текст песни
            </label>
            {songText && (
              <span className="text-xs text-green-600 font-medium">
                Можно отредактировать
              </span>
            )}
          </div>
          <textarea
            maxLength={LIMITS.SONG_TEXT}
            value={songText}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder="Вставьте свой текст или сгенерируйте его выше..."
            rows={8}
            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none resize-none text-gray-900"
          />
          <div className="flex justify-end mt-1">
            <span className="text-xs text-gray-400 font-medium">
              {songText.length}/{LIMITS.SONG_TEXT}
            </span>
          </div>
        </div>

        <button
          disabled={!canProceed}
          onClick={onNext}
          className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all"
        >
          Далее: Выбрать стиль
        </button>
      </div>
    </motion.div>
  );
}
